// Cross-feature CSV / PDF download helpers — shared by GA4, Google Ads, Event
// Intelligence, and Competitive Intelligence. Previously duplicated in the GA4
// and Google Ads shared/format.js (the GA4 copy named its CSV-cell param `cell`,
// the Ads copy `value` — identical output); this is the single source.
//
// No React. Pair with ./ExportMenu for the trigger UI.

// jspdf + jspdf-autotable are heavy and only needed when a PDF is actually
// requested, so downloadPdf imports them on demand rather than at module load.

export const downloadCsv = (headers, rows, filename) => {
  const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  // CRLF row endings (RFC 4180). Excel on Windows is the strictest consumer and expects
  // them; every other spreadsheet accepts them too.
  const csv = [headers, ...rows].map((row) => row.map(escape).join(',')).join('\r\n');
  // Lead with a UTF-8 BOM. Without it Excel opens the file in the machine's legacy code
  // page — em dashes and curly quotes in search terms garble — and frequently refuses it
  // outright ("the file format and extension don't match … could be corrupted"). The BOM
  // makes Excel read it as UTF-8 and as a genuine CSV; the charset in the MIME type does
  // the same for browsers that honour it.
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  // Firefox and Safari only fire a download for an anchor that is actually in the
  // document; a click on a detached anchor is ignored there, so the export either does
  // nothing or opens as text in a new tab — the usual reason it never lands as a file.
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoke on the next tick. Revoking synchronously can cut the download off before the
  // browser has finished reading the blob, producing a truncated, unopenable file.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

// Single-spec export factory. One column list drives BOTH the CSV and the PDF,
// so the two can't silently drift (the long-standing hazard: 16 sites hand-rolled
// a CSV header/cell array and a separate PDF header/cell array, kept in lockstep
// by hand). Proven shape lifted from the EI OpportunityBoardPanel / CI
// MarketPositionPanel EXPORT_COLUMNS specs.
//
// A column is:
//   { header, get, pdfHeader?, pdf? }
//     header     — CSV column header (string)
//     get        — (row, index) => CSV cell. Should return a plain string/number.
//     pdfHeader? — PDF header when it differs (e.g. abbreviated "Impr." for width).
//                  Defaults to `header`.
//     pdf?       — (row, index) => PDF cell when it differs from the CSV cell
//                  (e.g. CSV emits the raw number, PDF emits a formatted string).
//                  Defaults to `get`.
//
// Returns { headers, pdfHeaders, toCsvRow, toPdfRow } — feed straight into
// downloadCsv / downloadPdf. When CSV and PDF don't diverge, omit pdfHeader/pdf
// and both sides share the one definition.
export const makeExport = (columns) => ({
  headers: columns.map((column) => column.header),
  pdfHeaders: columns.map((column) => column.pdfHeader ?? column.header),
  toCsvRow: (row, index) => columns.map((column) => column.get(row, index)),
  toPdfRow: (row, index) => columns.map((column) => (column.pdf ?? column.get)(row, index)),
});

export const downloadPdf = async (title, headRow, bodyRows, filename, dateRange) => {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  doc.setFontSize(14);
  doc.text(title, 40, 40);
  if (dateRange) {
    doc.setFontSize(9);
    doc.text(dateRange, 40, 56);
  }
  autoTable(doc, {
    startY: dateRange ? 70 : 55,
    head: [headRow],
    body: bodyRows,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [50, 177, 255] },
    theme: 'grid',
    margin: { left: 40, right: 40 },
  });
  doc.save(filename);
};
